/**
 * BatchMatrixRowExpanded - Extended settings for Matrix Row
 *
 * Mode-aware: shows mode toggle and conditional content per mode.
 */

import { FileText, Layers, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select } from '@/components/v2/ui/Select';
import { GenerationSettingsControls } from './GenerationSettingsControls';
import { cn } from '@/lib/utils';
import type { MatrixItem, BatchMode } from '@/lib/api/batch';

interface Template {
  id: string;
  name: string;
}

interface Collection {
  id: string;
  name: string;
}

const RESOLUTIONS = [
  { label: '1k (1024)', value: 1024 as const },
  { label: '2k (2048)', value: 2048 as const },
  { label: '4k (4096)', value: 4096 as const },
];

const MODE_OPTIONS: Array<{ value: BatchMode; icon: typeof FileText }> = [
  { value: 'template', icon: FileText },
  { value: 'collection', icon: Layers },
  { value: 'prompt', icon: MessageSquare },
];

interface BatchMatrixRowExpandedProps {
  item: MatrixItem;
  templates: Template[];
  collections: Collection[];
  onChange: (item: MatrixItem) => void;
  onSelectFile: () => void;
  onSelectFromGallery: () => void;
  disabled?: boolean;
}

export function BatchMatrixRowExpanded({
  item,
  templates,
  collections,
  onChange,
  disabled = false,
}: BatchMatrixRowExpandedProps) {
  const { t } = useTranslation();
  const itemMode: BatchMode = item.mode || 'template';
  const isCollectionMode = itemMode === 'collection';

  const handleModeChange = (newMode: BatchMode) => {
    const updated: MatrixItem = {
      ...item,
      mode: newMode,
      aspect_ratios: newMode === 'collection' ? ['1:1'] : item.aspect_ratios,
      variants: newMode === 'collection' ? 1 : item.variants,
    };
    onChange(updated);
  };

  return (
    <div className="px-4 py-4 border-t border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/50 space-y-3">
      {/* Mode Toggle */}
      <div className="flex gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-lg w-fit">
        {MODE_OPTIONS.map(({ value, icon: Icon }) => (
          <button key={value} type="button" onClick={() => handleModeChange(value)} disabled={disabled}
            className={cn('flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-all', itemMode === value ? 'bg-white dark:bg-neutral-700 text-orange-600 dark:text-orange-400 shadow-sm' : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300', disabled && 'opacity-50 cursor-not-allowed')}>
            <Icon className="h-3.5 w-3.5" />
            {value === 'template' ? 'Template' : value === 'collection' ? 'Collection' : 'Manuell'}
          </button>
        ))}
      </div>

      {/* Template Mode Content */}
      {itemMode === 'template' && (
        <>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Template</label>
            <Select
              options={[{ value: '__none__', label: t('batch.templateChoose') }, ...templates.map((tmpl) => ({ value: tmpl.id, label: tmpl.name }))]}
              value={item.template_id || '__none__'}
              onValueChange={(v) => onChange({ ...item, template_id: v === '__none__' ? undefined : v })}
              disabled={disabled}
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Beschreibung (optional)</label>
            <textarea value={item.custom_prompt || ''} onChange={(e) => onChange({ ...item, custom_prompt: e.target.value })} disabled={disabled} placeholder={t('batch.customPromptPlaceholder')}
              className={cn('w-full px-3 py-2 rounded-lg border text-sm resize-vertical min-h-[60px] bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-600 placeholder:text-neutral-400', disabled && 'opacity-50 cursor-not-allowed')} />
          </div>
        </>
      )}

      {/* Collection Mode Content */}
      {itemMode === 'collection' && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Collection</label>
          <Select
            options={[{ value: '__none__', label: t('batch.collectionChoose') }, ...collections.map((c) => ({ value: c.id, label: c.name }))]}
            value={item.collection_id || '__none__'}
            onValueChange={(v) => onChange({ ...item, collection_id: v === '__none__' ? undefined : v })}
            disabled={disabled}
          />
          <p className="text-xs text-neutral-500">Die Collection steuert Aspect Ratios, Variants und Prompts.</p>
        </div>
      )}

      {/* Prompt Mode Content */}
      {itemMode === 'prompt' && (
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Custom Prompt</label>
          <textarea value={item.custom_prompt || ''} onChange={(e) => onChange({ ...item, custom_prompt: e.target.value })} disabled={disabled} placeholder={t('batch.batchPromptPlaceholder')}
            className={cn('w-full px-3 py-2 rounded-lg border text-sm resize-vertical min-h-[80px] bg-white dark:bg-neutral-800 border-neutral-200 dark:border-neutral-600 placeholder:text-neutral-400', disabled && 'opacity-50 cursor-not-allowed')} />
        </div>
      )}

      {/* Generation Settings (not shown for collection) */}
      {!isCollectionMode && (
        <GenerationSettingsControls
          aspectRatios={item.aspect_ratios || ['1:1']}
          variants={item.variants || 1}
          seedMode={item.seed === null ? 'auto' : 'fixed'}
          onAspectRatiosChange={(v) => onChange({ ...item, aspect_ratios: v })}
          onVariantsChange={(v) => onChange({ ...item, variants: v })}
          onSeedModeChange={(v) => onChange({ ...item, seed: v === 'auto' ? null : 12345 })}
          disabled={disabled}
        />
      )}

      {/* Output Settings */}
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{t('batch.formatQuality')}</label>
        <div className="flex items-center gap-3">
          <Select options={[{ value: 'jpeg', label: 'JPEG' }, { value: 'png', label: 'PNG' }, { value: 'webp', label: 'WebP' }]}
            value={item.output_format || 'jpeg'} onValueChange={(v) => onChange({ ...item, output_format: v as 'jpeg' | 'png' | 'webp' })} disabled={disabled} />
          <span className="text-xs text-neutral-500">{t('batch.qualityLabel')}</span>
          <input type="range" min="1" max="100" value={item.output_quality || 90}
            onChange={(e) => onChange({ ...item, output_quality: parseInt(e.target.value) })} disabled={disabled} className="flex-1" />
          <span className="text-xs font-medium text-neutral-700 dark:text-neutral-300 min-w-[3ch]">{item.output_quality || 90}%</span>
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">{t('batch.resolutionLabel')}</label>
        <div className="flex gap-2">
          {RESOLUTIONS.map((res) => (
            <button key={res.value} type="button" onClick={() => onChange({ ...item, output_resolution: res.value })} disabled={disabled}
              className={cn('px-4 py-2 rounded text-sm font-medium transition-all', item.output_resolution === res.value ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400', disabled && 'opacity-50 cursor-not-allowed')}>
              {res.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
